import { useEffect, useState, useCallback, useRef } from "react";
import { Loader2, Plus, Trash2, Video, Calendar, Stethoscope, Pencil, Copy, BellRing, Facebook, Film, Download, Ban, Send, RefreshCw, Wallet, Sparkles, DoorOpen, DoorClosed } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { AiCompare } from "./AiCompare";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { TeleconsultationManager } from "./TeleconsultationManager";

const TYPE_COLORS = ["#059669", "#2563EB", "#7C3AED", "#F59E0B", "#0EA5E9", "#DB2777"];

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const TYPES = [
  { key: "teleconsultation", label: "Téléconsultation" },
  { key: "formation", label: "Formation" },
  { key: "expertise", label: "Expertise" },
  { key: "webinaire", label: "Webinaire" },
];
const typeLabel = (k) => TYPES.find((t) => t.key === k)?.label || k;
const STATUS = { confirme: "Confirmé", en_cours: "En cours", termine: "Terminé", annule: "Annulé" };

function KPI({ label, value, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={testid}>
      <p className="font-mono text-[11px] uppercase tracking-widest text-slate-400">{label}</p>
      <p className="mt-2 font-display text-xl font-bold text-ciel">{value}</p>
    </div>
  );
}

function VisioMarkdown({ text }) {
  const lines = (text || "").split("\n");
  const out = [];
  let bullets = [];
  const flush = (i) => { if (bullets.length) { out.push(<ul key={`u${i}`} className="my-2 ml-4 list-disc space-y-1">{bullets}</ul>); bullets = []; } };
  const inline = (s) => s.split(/(\*\*[^*]+\*\*)/g).map((p, j) => p.startsWith("**") && p.endsWith("**")
    ? <strong key={j} className="text-navy">{p.slice(2, -2)}</strong> : <span key={j}>{p}</span>);
  lines.forEach((ln, i) => {
    const t = ln.trim();
    if (t === "---" || t === "***") { flush(i); return; }
    if (t.startsWith("## ")) { flush(i); out.push(<h4 key={i} className="mt-4 mb-1 font-display text-base font-semibold text-navy">{t.slice(3)}</h4>); }
    else if (t.startsWith("### ")) { flush(i); out.push(<h5 key={i} className="mt-3 mb-1 font-semibold text-navy">{t.slice(4)}</h5>); }
    else if (t.startsWith("# ")) { flush(i); out.push(<h4 key={i} className="mt-1 mb-2 font-display text-lg font-bold text-navy">{t.slice(2)}</h4>); }
    else if (t.startsWith("- ") || t.startsWith("* ")) { bullets.push(<li key={i} className="text-sm text-slate-600">{inline(t.slice(2))}</li>); }
    else if (t) { flush(i); out.push(<p key={i} className="my-1 text-sm text-slate-600">{inline(t)}</p>); }
  });
  flush("end");
  return <div data-testid="visio-ai-content">{out}</div>;
}

function ModeBadge({ label, mode, testid }) {
  const real = mode === "réel";
  return (
    <span data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${real ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}>
      <span className={`h-2 w-2 rounded-full ${real ? "bg-emerald-500" : "bg-amber-500"}`} />
      {label} : {mode || "?"}
    </span>
  );
}

export function VisioManager() {
  const [stats, setStats] = useState(null);
  const [slots, setSlots] = useState([]);
  const [appts, setAppts] = useState([]);
  const [slotForm, setSlotForm] = useState({ type: "teleconsultation", start: "", duration_min: 30, title: "" });
  const [busy, setBusy] = useState(false);
  const [editSlot, setEditSlot] = useState(null); // {id, type, start, duration_min, max_participants, title}
  const [remChoices, setRemChoices] = useState([]);
  const [remSettings, setRemSettings] = useState({ offsets: [], email: true, whatsapp: true, sms: true });
  const [editRem, setEditRem] = useState(null); // {aid, name, offsets, email, whatsapp}
  const [recDialog, setRecDialog] = useState(null); // {name, recordings, message}
  const [editAppt, setEditAppt] = useState(null); // {id, type, start, duration_min, name, email, phone, whatsapp}
  const [crLinkDays, setCrLinkDays] = useState(7);
  const [prices, setPrices] = useState(null);
  const [avail, setAvail] = useState(null);
  const [revMonthly, setRevMonthly] = useState([]);
  const [ai, setAi] = useState({ status: "none", analysis: "", generated_at: "" });
  const [aiBusy, setAiBusy] = useState(false);
  const [aiReport, setAiReport] = useState({ enabled: false, email: "", frequency: "weekly", last_sent: "", llm_available: true });
  const [reportBusy, setReportBusy] = useState(false);
  const [payRemindBusy, setPayRemindBusy] = useState(false);
  const pollRef = useRef(null);
  const [payFilter, setPayFilter] = useState("all");
  const [apptSearch, setApptSearch] = useState("");
  const filteredAppts = appts.filter((a) => {
    const price = a.price_xof || 0;
    if (payFilter === "paid" && !(price > 0 && a.paid)) return false;
    if (payFilter === "pending" && !(price > 0 && !a.paid)) return false;
    if (payFilter === "free" && price !== 0) return false;
    const q = apptSearch.trim().toLowerCase();
    if (q && !`${a.name || ""} ${a.email || ""}`.toLowerCase().includes(q)) return false;
    return true;
  });

  useEffect(() => { api.get("/admin/visio/prices").then((r) => setPrices(r.data)).catch(() => {});
    api.get("/admin/visio/availability").then((r) => setAvail(r.data)).catch(() => {});
    api.get("/admin/visio/revenue-monthly").then((r) => setRevMonthly(r.data.data || [])).catch(() => {});
    api.get("/admin/visio/ai-analysis").then((r) => setAi(r.data)).catch(() => {});
    api.get("/admin/visio/ai-report").then((r) => setAiReport(r.data)).catch(() => {}); }, []);
  const saveAiReport = async () => {
    setReportBusy(true);
    try {
      const { data } = await api.put("/admin/visio/ai-report", { enabled: aiReport.enabled, email: aiReport.email, frequency: aiReport.frequency });
      setAiReport((s) => ({ ...s, ...data }));
      toast.success("Réglages du rapport IA enregistrés");
    } catch (e) { toast.error(apiError(e)); } finally { setReportBusy(false); }
  };
  const sendReportNow = async () => {
    setReportBusy(true);
    try {
      const { data } = await api.post("/admin/visio/ai-report/send-now");
      toast.success(`Rapport IA envoyé à ${data.email}`);
      api.get("/admin/visio/ai-report").then((r) => setAiReport(r.data)).catch(() => {});
    } catch (e) { toast.error(apiError(e, "Envoi impossible")); } finally { setReportBusy(false); }
  };
  const remindUnpaid = async () => {
    setPayRemindBusy(true);
    try {
      const { data } = await api.post("/admin/visio/payment-reminders");
      const c = data.canaux || {};
      toast.success(`${data.relances}/${data.total_impayes} relance(s) — email ${c.email || 0}, WhatsApp ${c.whatsapp || 0} (${data.whatsapp_mode}), SMS ${c.sms || 0} (${data.sms_mode})`);
    } catch (e) { toast.error(apiError(e, "Relance impossible")); } finally { setPayRemindBusy(false); }
  };
  const pollAi = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get("/admin/visio/ai-analysis");
        setAi(data);
        if (data.status !== "running") { clearInterval(pollRef.current); setAiBusy(false); }
      } catch { /* ignore */ }
    }, 4000);
  }, []);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);
  const runAi = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/admin/visio/ai-analysis");
      setAi((a) => ({ ...a, status: "running" }));
      if (data.status === "running") pollAi();
    } catch (e) { toast.error(apiError(e, "Analyse impossible")); setAiBusy(false); }
  };
  const savePrices = async () => {
    setBusy(true);
    try { await api.put("/admin/visio/prices", { prices: prices.prices }); toast.success("Tarifs enregistrés"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const addRule = (t) => setAvail((a) => ({ ...a, rules: { ...a.rules, [t]: [...(a.rules[t] || []), { weekday: 1, time: "09:00", duration_min: 30, max_participants: 1 }] } }));
  const rmRule = (t, i) => setAvail((a) => ({ ...a, rules: { ...a.rules, [t]: a.rules[t].filter((_, k) => k !== i) } }));
  const setRule = (t, i, f, v) => setAvail((a) => ({ ...a, rules: { ...a.rules, [t]: a.rules[t].map((r, k) => k === i ? { ...r, [f]: v } : r) } }));
  const saveAvail = async () => {
    setBusy(true);
    try { const { data } = await api.put("/admin/visio/availability", { horizon_days: avail.horizon_days, rules: avail.rules }); toast.success(`Disponibilités enregistrées · ${data.generated} créneau(x) générés`); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const WEEKDAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
  const exportRevenue = async (kind) => {
    try { const res = await api.get(`/admin/visio/revenue/export/${kind}`, { responseType: "blob" }); const a = document.createElement("a"); a.href = URL.createObjectURL(res.data); a.download = `recettes-consultations.${kind === "pdf" ? "pdf" : "xlsx"}`; a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 4000); }
    catch (e) { toast.error(apiError(e, "Export impossible")); }
  };

  const load = useCallback(async () => {
    try {
      const [s, sl, ap, rs, vs] = await Promise.all([
        api.get("/admin/visio/stats"), api.get("/admin/visio/slots"), api.get("/admin/visio/appointments"),
        api.get("/admin/visio/reminder-settings"), api.get("/admin/visio/settings"),
      ]);
      setStats(s.data); setSlots(sl.data); setAppts(ap.data);
      setRemChoices(rs.data.choices || []);
      const g = rs.data.settings || {};
      setRemSettings({ offsets: g.offsets || [], email: g.email !== false, whatsapp: g.whatsapp !== false, sms: g.sms !== false });
      setCrLinkDays(vs.data.cr_link_days || 7);
    } catch (e) { toast.error(apiError(e, "Erreur de chargement visio")); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveCrLinkDays = async (days) => {
    setCrLinkDays(days);
    try {
      await api.put("/admin/visio/settings", { cr_link_days: days });
      toast.success(`Validité du lien du compte-rendu : ${days} jours`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleGlobalOffset = (min) => setRemSettings((r) => ({
    ...r, offsets: r.offsets.includes(min) ? r.offsets.filter((o) => o !== min) : [...r.offsets, min],
  }));
  const saveRemSettings = async () => {
    setBusy(true);
    try {
      await api.put("/admin/visio/reminder-settings", remSettings);
      toast.success("Réglages de rappels enregistrés");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const openRemEdit = (a) => {
    const rc = a.reminders || remSettings;
    setEditRem({ aid: a.id, name: a.name, offsets: rc.offsets || [], email: rc.email !== false, whatsapp: rc.whatsapp !== false, sms: rc.sms !== false, log: a.reminder_log || [] });
  };
  const toggleEditOffset = (min) => setEditRem((r) => ({
    ...r, offsets: r.offsets.includes(min) ? r.offsets.filter((o) => o !== min) : [...r.offsets, min],
  }));
  const saveRemEdit = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/visio/appointments/${editRem.aid}/reminders`,
        { offsets: editRem.offsets, email: editRem.email, whatsapp: editRem.whatsapp, sms: editRem.sms });
      toast.success("Rappels de la session mis à jour"); setEditRem(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const addSlot = async () => {
    if (!slotForm.start) { toast.error("Choisissez une date/heure"); return; }
    setBusy(true);
    try {
      await api.post("/admin/visio/slots", { ...slotForm, start: new Date(slotForm.start).toISOString() });
      toast.success("Créneau ajouté"); setSlotForm({ ...slotForm, start: "" }); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const delSlot = async (id) => { try { await api.delete(`/admin/visio/slots/${id}`); load(); } catch (e) { toast.error(apiError(e)); } };
  const genRecurring = async () => { setBusy(true); try { const { data } = await api.post("/admin/visio/availability/generate"); toast.success(`${data.generated} créneau(x) récurrents générés`); load(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); } };
  const publishFacebook = async (sid) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/visio/slots/${sid}/facebook`);
      toast.success(data.mode === "simulé" ? "Publié (mode SIMULÉ — clés Meta en attente)" : "Publié sur Facebook");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const openRecordings = async (a) => {
    setRecDialog({ name: a.name, recordings: [], message: "Chargement…" });
    try {
      const { data } = await api.get(`/admin/visio/appointments/${a.id}/recordings`);
      setRecDialog({ name: a.name, recordings: data.recordings || [], message: data.message || "" });
    } catch (e) { setRecDialog({ name: a.name, recordings: [], message: apiError(e) }); }
  };
  const downloadRec = async (recId) => {
    try {
      const { data } = await api.get(`/admin/visio/recordings/${recId}/link`);
      if (data.download_link) window.open(data.download_link, "_blank");
      else toast.info("Lien indisponible");
    } catch (e) { toast.error(apiError(e)); }
  };
  const openEdit = (s) => setEditSlot({ id: s.id, type: s.type, start: toLocalInput(s.start), duration_min: s.duration_min, max_participants: s.max_participants, title: s.title || "" });
  const saveEdit = async () => {
    if (!editSlot.start) { toast.error("Choisissez une date/heure"); return; }
    setBusy(true);
    try {
      await api.patch(`/admin/visio/slots/${editSlot.id}`, {
        type: editSlot.type, start: new Date(editSlot.start).toISOString(),
        duration_min: editSlot.duration_min, max_participants: editSlot.max_participants, title: editSlot.title,
      });
      toast.success("Activité mise à jour"); setEditSlot(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const setStatus = async (id, status) => { try { await api.patch(`/admin/visio/appointments/${id}/status`, { status }); load(); } catch (e) { toast.error(apiError(e)); } };
  const joinHost = async (id, url) => {
    try {
      const { data } = await api.post(`/admin/visio/appointments/${id}/host-token`);
      if (data.simulated) { toast.info("Mode simulé : la salle réelle nécessite la clé Daily.co."); return; }
      window.open(`${data.room_url}?t=${data.token}`, "_blank");
    } catch (e) { toast.error(apiError(e)); }
  };
  const openEditAppt = (a) => setEditAppt({ id: a.id, type: a.type, start: toLocalInput(a.start), duration_min: a.duration_min, name: a.name || "", email: a.email || "", phone: a.phone || "", whatsapp: a.whatsapp || "", auto_open_min: a.auto_open_min || 0, preopen_reminder_min: a.preopen_reminder_min || 10 });
  const saveEditAppt = async () => {
    if (!editAppt.start) { toast.error("Choisissez une date/heure"); return; }
    setBusy(true);
    try {
      await api.patch(`/admin/visio/appointments/${editAppt.id}`, {
        type: editAppt.type, start: new Date(editAppt.start).toISOString(), duration_min: Number(editAppt.duration_min) || 30,
        name: editAppt.name, email: editAppt.email, phone: editAppt.phone, whatsapp: editAppt.whatsapp,
        auto_open_min: Number(editAppt.auto_open_min) || 0, preopen_reminder_min: Number(editAppt.preopen_reminder_min) || 10,
      });
      toast.success("Rendez-vous modifié — participants notifiés"); setEditAppt(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const cancelAppt = async (a) => {
    if (!window.confirm(`Annuler le rendez-vous de ${a.name} ? Une notification sera envoyée.`)) return;
    try { const { data } = await api.post(`/admin/visio/appointments/${a.id}/cancel`); toast.success(`Annulé — notification ${data.whatsapp_mode === "simulé" ? "email envoyée (WhatsApp simulé)" : "envoyée"}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const notifyAppt = async (a) => {
    try { const { data } = await api.post(`/admin/visio/appointments/${a.id}/notify`); toast.success(`Infos de connexion renvoyées${data.whatsapp_mode === "simulé" ? " (WhatsApp simulé)" : ""}`); }
    catch (e) { toast.error(apiError(e)); }
  };
  const openRoomNow = async (a) => {
    if (!window.confirm(`Ouvrir la salle maintenant pour ${a.name} ? Le participant sera prévenu (email + SMS/WhatsApp) que la salle est disponible.`)) return;
    try {
      const { data } = await api.post(`/admin/visio/appointments/${a.id}/open-now`);
      const chans = [data.email_sent && "email", data.sms_sent && "SMS", data.whatsapp_sent && "WhatsApp"].filter(Boolean).join(" + ") || "notification (simulée)";
      toast.success(`Salle ouverte — participant prévenu (${chans})`); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const closeRoomNow = async (a) => {
    if (!window.confirm(`Fermer la salle de ${a.name} ? L'accès sera révoqué et la session clôturée.`)) return;
    try { await api.post(`/admin/visio/appointments/${a.id}/close-now`); toast.success("Salle fermée — session clôturée"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const reopenRoom = async (a) => {
    if (!window.confirm(`Réouvrir la salle de ${a.name} ? Le participant sera de nouveau autorisé et prévenu.`)) return;
    try { await api.post(`/admin/visio/appointments/${a.id}/reopen`); toast.success("Salle réouverte — participant prévenu"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const fmt = (iso) => new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });

  if (!stats) return <div className="flex min-h-[40vh] items-center justify-center"><Loader2 className="animate-spin text-ciel" /></div>;

  return (
    <div data-testid="visio-manager">
      <div className="mb-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KPI label="Sessions ce mois" value={stats.sessions_mois} testid="visio-kpi-mois" />
        <KPI label="Téléconsultations (mois)" value={stats.teleconsultations_mois} testid="visio-kpi-teleconsult" />
        <KPI label="Durée totale (min)" value={stats.duree_totale_min} testid="visio-kpi-duree" />
        <KPI label="Mode Daily.co" value={stats.daily_mode} testid="visio-kpi-mode" />
      </div>

      {stats.revenue && (
        <div className="mb-6 grid gap-4 sm:grid-cols-3" data-testid="visio-revenue">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4">
            <div className="flex items-center gap-2 text-xs font-medium text-emerald-700"><Wallet size={14} /> Recettes ce mois</div>
            <p className="mt-1 font-display text-2xl font-bold text-emerald-700" data-testid="visio-rev-mois">{new Intl.NumberFormat("fr-FR").format(stats.revenue.encaisse_mois)} F</p>
          </div>
          <div className="rounded-xl border border-navy/15 bg-navy/[0.03] p-4">
            <div className="flex items-center gap-2 text-xs font-medium text-navy"><Wallet size={14} /> Recettes cumulées</div>
            <p className="mt-1 font-display text-2xl font-bold text-navy" data-testid="visio-rev-total">{new Intl.NumberFormat("fr-FR").format(stats.revenue.encaisse)} F</p>
            <p className="text-[11px] text-slate-500">{stats.revenue.nb_payes} consultation(s) réglée(s)</p>
          </div>
          <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4">
            <div className="flex items-center gap-2 text-xs font-medium text-amber-700"><Wallet size={14} /> En attente de règlement</div>
            <p className="mt-1 font-display text-2xl font-bold text-amber-700" data-testid="visio-rev-pending">{new Intl.NumberFormat("fr-FR").format(stats.revenue.en_attente)} F</p>
            <p className="text-[11px] text-slate-500">{stats.revenue.nb_en_attente} à régler</p>
            {stats.revenue.nb_en_attente > 0 && (
              <Button size="sm" onClick={remindUnpaid} disabled={payRemindBusy} data-testid="visio-remind-unpaid"
                className="mt-2 h-7 rounded-full bg-amber-600 px-3 text-[11px] text-white hover:bg-amber-700">
                {payRemindBusy ? <><Loader2 size={12} className="mr-1 animate-spin" /> Relance…</> : <><Send size={12} className="mr-1" /> Relancer (email + WhatsApp/SMS)</>}
              </Button>
            )}
          </div>
        </div>
      )}

      {stats.revenue?.par_type?.some((t) => t.encaisse || t.en_attente) && (
        <div className="mb-6 grid gap-4 lg:grid-cols-5" data-testid="visio-revenue-by-type">
          <div className="rounded-xl border border-slate-200 bg-white p-5 lg:col-span-2">
            <p className="mb-1 flex items-center gap-2 text-sm font-semibold text-navy"><Wallet size={15} /> Recettes par type de consultation</p>
            <p className="mb-3 text-xs text-slate-500">Répartition du CA encaissé par type.</p>
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={stats.revenue.par_type.filter((t) => t.encaisse > 0)} dataKey="encaisse" nameKey="label"
                     cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={2}>
                  {stats.revenue.par_type.filter((t) => t.encaisse > 0).map((t, i) => (
                    <Cell key={t.type} fill={TYPE_COLORS[i % TYPE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v, n) => [new Intl.NumberFormat("fr-FR").format(v) + " F", n]} contentStyle={{ fontSize: 12, borderRadius: 10, border: "1px solid #E2E8F0" }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:col-span-3 lg:grid-cols-2">
            {stats.revenue.par_type.map((t, i) => (
              <div key={t.type} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`visio-rev-type-${t.type}`}>
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: TYPE_COLORS[i % TYPE_COLORS.length] }} />
                  <p className="text-sm font-semibold text-navy">{t.label}</p>
                </div>
                <p className="mt-2 font-display text-xl font-bold text-emerald-700">{new Intl.NumberFormat("fr-FR").format(t.encaisse)} F</p>
                <p className="text-[11px] text-slate-500">{t.nb_payes} réglée(s)</p>
                {t.en_attente > 0 && (
                  <p className="mt-1 text-[11px] font-medium text-amber-700">{new Intl.NumberFormat("fr-FR").format(t.en_attente)} F en attente ({t.nb_en_attente})</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mb-6 rounded-2xl border border-violet-200 bg-violet-50/40 p-4" data-testid="visio-ai-panel">
        <div className="flex flex-wrap items-center gap-2">
          <Sparkles size={16} className="text-violet-600" />
          <h3 className="font-display text-sm font-semibold text-navy">Analyse IA — recettes & activité</h3>
          {ai.status === "done" && ai.generated_at && <span className="text-[11px] text-slate-400">Générée · {new Date(ai.generated_at).toLocaleString("fr-FR")}</span>}
          <Button size="sm" onClick={runAi} disabled={aiBusy || ai.status === "running"} data-testid="visio-ai-run"
            className="ml-auto rounded-full bg-violet-600 px-3 text-xs text-white hover:bg-violet-700">
            {(aiBusy || ai.status === "running") ? <><Loader2 size={13} className="mr-1 animate-spin" /> Analyse en cours…</> : <><Sparkles size={13} className="mr-1" /> Analyser avec l'IA</>}
          </Button>
          <AiCompare moduleKey="visio" params={{}} />
        </div>
        {(aiBusy || ai.status === "running") && <p className="mt-2 text-xs text-slate-500">L'IA agrège les recettes par type, la présence, les no-shows et les impayés, puis rédige des recommandations (~30 s)…</p>}
        {ai.status === "error" && <p className="mt-2 text-xs text-red-500">Erreur : {ai.error}</p>}
        {ai.status === "done" && ai.analysis && <div className="mt-3 rounded-xl bg-white p-4"><VisioMarkdown text={ai.analysis} /></div>}
        {ai.status === "none" && !aiBusy && <p className="mt-2 text-xs text-slate-400">Lancez l'analyse pour obtenir une synthèse chiffrée (recettes par type, taux de présence, impayés) avec des recommandations priorisées.</p>}

        <div className="mt-4 rounded-xl border border-violet-200 bg-white/70 p-3" data-testid="visio-ai-report-config">
          <div className="flex flex-wrap items-center gap-2">
            <BellRing size={14} className="text-violet-600" />
            <span className="text-xs font-semibold text-navy">Rapport IA automatique par email</span>
            <label className="ml-auto flex items-center gap-2 text-xs text-slate-600">
              <Switch checked={aiReport.enabled} onCheckedChange={(v) => setAiReport((s) => ({ ...s, enabled: v }))} data-testid="visio-ai-report-toggle" />
              {aiReport.enabled ? "Activé" : "Désactivé"}
            </label>
          </div>
          <p className="mt-1 text-[11px] text-slate-500">Envoie automatiquement l'analyse IA (recettes, présence, impayés) à la direction, {aiReport.frequency === "monthly" ? "le 1er de chaque mois" : "chaque lundi matin"}.</p>
          <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_auto]">
            <Input type="email" value={aiReport.email} onChange={(e) => setAiReport((s) => ({ ...s, email: e.target.value }))}
              placeholder="direction@cabinet.ci" data-testid="visio-ai-report-email" className="h-9 text-sm" />
            <Select value={aiReport.frequency} onValueChange={(v) => setAiReport((s) => ({ ...s, frequency: v }))}>
              <SelectTrigger className="h-9 w-full text-sm sm:w-44" data-testid="visio-ai-report-freq"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="weekly">Hebdomadaire (lundi)</SelectItem>
                <SelectItem value="monthly">Mensuel (1er du mois)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={saveAiReport} disabled={reportBusy} data-testid="visio-ai-report-save"
              className="h-8 rounded-full bg-violet-600 px-3 text-xs text-white hover:bg-violet-700">
              {reportBusy ? <Loader2 size={13} className="animate-spin" /> : "Enregistrer"}
            </Button>
            <Button size="sm" variant="outline" onClick={sendReportNow} disabled={reportBusy || !aiReport.email} data-testid="visio-ai-report-send-now"
              className="h-8 rounded-full px-3 text-xs">
              <Send size={13} className="mr-1" /> Envoyer maintenant
            </Button>
            {aiReport.last_sent && <span className="text-[11px] text-slate-400">Dernier envoi · {new Date(aiReport.last_sent).toLocaleString("fr-FR")}</span>}
            {!aiReport.llm_available && <span className="text-[11px] text-amber-600">Clé IA indisponible en preview</span>}
          </div>
        </div>
      </div>

      {revMonthly.some((m) => m.encaisse || m.en_attente) && (
        <div className="mb-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="visio-revenue-chart">
          <p className="mb-1 flex items-center gap-2 text-sm font-semibold text-navy"><Wallet size={15} /> Évolution des recettes par mois (FCFA)
            <span className="ml-auto flex gap-1.5">
              <button onClick={() => exportRevenue("pdf")} data-testid="visio-rev-export-pdf" className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-200">PDF</button>
              <button onClick={() => exportRevenue("excel")} data-testid="visio-rev-export-excel" className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-200">Excel</button>
            </span>
          </p>
          <p className="mb-4 text-xs text-slate-500">Encaissé vs en attente de règlement, par mois de consultation.</p>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={revMonthly} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748B" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(v) => new Intl.NumberFormat("fr-FR", { notation: "compact" }).format(v)} />
              <Tooltip formatter={(v) => new Intl.NumberFormat("fr-FR").format(v) + " F"} contentStyle={{ fontSize: 12, borderRadius: 10, border: "1px solid #E2E8F0" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="encaisse" name="Encaissé" stackId="a" fill="#059669" radius={[4, 4, 0, 0]} />
              <Bar dataKey="en_attente" name="En attente" stackId="a" fill="#F59E0B" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="mb-6 flex flex-wrap items-center gap-2" data-testid="visio-integration-status">
        <span className="text-xs font-medium text-slate-400">Intégrations :</span>
        <ModeBadge label="Daily.co (vidéo)" mode={stats.daily_mode} testid="visio-badge-daily" />
        <ModeBadge label="SMS" mode={stats.sms_mode} testid="visio-badge-sms" />
        <ModeBadge label="WhatsApp" mode={stats.whatsapp_mode} testid="visio-badge-whatsapp" />
      </div>

      <Tabs defaultValue="teleconsultations">
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger value="teleconsultations" data-testid="visio-tab-teleconsultations">Téléconsultations</TabsTrigger>
          <TabsTrigger value="sessions" data-testid="visio-tab-sessions">Toutes les sessions</TabsTrigger>
          <TabsTrigger value="dispo" data-testid="visio-tab-dispo">Disponibilités</TabsTrigger>
          <TabsTrigger value="rappels" data-testid="visio-tab-rappels">Rappels</TabsTrigger>
        </TabsList>

        <TabsContent value="teleconsultations" className="mt-6">
          <TeleconsultationManager appts={appts} reload={load} stats={stats} />
        </TabsContent>

        <TabsContent value="sessions" className="mt-6">
          <div className="mb-3 flex flex-wrap items-center gap-1.5" data-testid="visio-pay-filter">
            {[["all", "Toutes"], ["paid", "Payées"], ["pending", "À régler"], ["free", "Gratuites"]].map(([k, l]) => (
              <button key={k} onClick={() => setPayFilter(k)} data-testid={`visio-pay-filter-${k}`}
                className={`rounded-full px-3 py-1 text-xs font-medium ${payFilter === k ? "bg-navy text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>
            ))}
            <Input value={apptSearch} onChange={(e) => setApptSearch(e.target.value)} data-testid="visio-appt-search"
              placeholder="Rechercher un patient (nom ou email)…" className="ml-auto h-8 w-64 text-xs" />
          </div>
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="visio-appts">
            <table className="w-full min-w-[820px] text-sm">
              <thead><tr className="border-b border-slate-200 text-left font-mono text-[11px] uppercase tracking-wider text-slate-400">
                <th className="p-3">Date</th><th className="p-3">Type</th><th className="p-3">Participant</th><th className="p-3">Motif</th><th className="p-3">Paiement</th><th className="p-3">Statut</th><th className="p-3"></th>
              </tr></thead>
              <tbody>
                {filteredAppts.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-400">Aucune session.</td></tr>}
                {filteredAppts.map((a) => (
                  <tr key={a.id} className="border-b border-slate-100" data-testid={`visio-appt-${a.id}`}>
                    <td className="p-3 text-xs text-slate-500">{fmt(a.start)}</td>
                    <td className="p-3 text-xs">{typeLabel(a.type)}</td>
                    <td className="p-3">{a.name}<br /><span className="text-xs text-slate-400">{a.email}</span>
                      <br /><span className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${a.open_early && a.status === "en_cours" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid={`visio-room-state-${a.id}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${a.open_early && a.status === "en_cours" ? "bg-emerald-500" : "bg-slate-400"}`} />{a.open_early && a.status === "en_cours" ? "Salle ouverte" : "Salle fermée"}
                      </span></td>
                    <td className="max-w-[160px] truncate p-3 text-xs text-slate-500">{a.motif}</td>
                    <td className="p-3 text-xs" data-testid={`visio-appt-pay-${a.id}`}>
                      {a.price_xof > 0 ? (
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${a.paid ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                          {a.paid ? "Payé" : "À régler"} · {new Intl.NumberFormat("fr-FR").format(a.price_xof)} F
                        </span>
                      ) : <span className="text-slate-300">Gratuit</span>}
                    </td>
                    <td className="p-3">
                      <Select value={a.status} onValueChange={(v) => setStatus(a.id, v)}>
                        <SelectTrigger className="h-7 w-28 text-xs" data-testid={`visio-status-${a.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="p-3">
                      {a.type === "teleconsultation" && (
                        <Button size="sm" className="mr-1 rounded-full bg-navy text-xs text-white hover:bg-navy-light" onClick={() => window.open(`/admin/visio/consultation/${a.id}`, "_blank", "noopener,noreferrer")} data-testid={`visio-consult-${a.id}`}>
                          <Stethoscope size={13} className="mr-1" /> Consulter
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => joinHost(a.id, a.room_url)} data-testid={`visio-join-${a.id}`}>
                        <Video size={13} className="mr-1" /> Rejoindre
                      </Button>
                      {a.status !== "annule" && a.status !== "termine" && (
                        <Button size="sm" className="ml-1 rounded-full bg-emerald-600 text-xs text-white hover:bg-emerald-700" data-testid={`visio-open-now-${a.id}`} onClick={() => openRoomNow(a)}>
                          <DoorOpen size={13} className="mr-1" /> Ouvrir la salle maintenant
                        </Button>
                      )}
                      {a.status !== "annule" && a.status !== "termine" && (
                        <Button size="sm" variant="outline" className="ml-1 rounded-full border-slate-200 text-xs text-slate-600" data-testid={`visio-close-now-${a.id}`} onClick={() => closeRoomNow(a)}>
                          <DoorClosed size={13} className="mr-1" /> Fermer la salle
                        </Button>
                      )}
                      {a.status === "termine" && (
                        <Button size="sm" className="ml-1 rounded-full bg-amber-500 text-xs text-white hover:bg-amber-600" data-testid={`visio-reopen-${a.id}`} onClick={() => reopenRoom(a)}>
                          <DoorOpen size={13} className="mr-1" /> Réouvrir la salle
                        </Button>
                      )}
                      {a.join_token && (
                        <Button size="sm" variant="outline" className="ml-1 rounded-full text-xs" data-testid={`visio-copy-${a.id}`}
                          onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/visio/${a.join_token}`); toast.success("Lien copié" + (a.pin ? ` · PIN ${a.pin}` : "")); }}>
                          <Copy size={13} className="mr-1" /> Copier le lien
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="ml-1 rounded-full text-xs" data-testid={`visio-rem-${a.id}`} onClick={() => openRemEdit(a)}>
                        <BellRing size={13} className="mr-1" /> Rappels
                      </Button>
                      <Button size="sm" variant="outline" className="ml-1 rounded-full text-xs" data-testid={`visio-rec-${a.id}`} onClick={() => openRecordings(a)}>
                        <Film size={13} className="mr-1" /> Enreg.
                      </Button>
                      <Button size="sm" variant="outline" className="ml-1 rounded-full text-xs" data-testid={`visio-edit-appt-${a.id}`} onClick={() => openEditAppt(a)}>
                        <Pencil size={13} className="mr-1" /> Modifier
                      </Button>
                      <Button size="sm" variant="outline" className="ml-1 rounded-full text-xs" data-testid={`visio-notify-${a.id}`} onClick={() => notifyAppt(a)}>
                        <Send size={13} className="mr-1" /> Rappel
                      </Button>
                      {a.status !== "annule" && (
                        <Button size="sm" variant="outline" className="ml-1 rounded-full border-red-200 text-xs text-red-600 hover:bg-red-50" data-testid={`visio-cancel-${a.id}`} onClick={() => cancelAppt(a)}>
                          <Ban size={13} className="mr-1" /> Annuler
                        </Button>
                      )}
                      {(a.room_events && a.room_events.length > 0) && (
                        <div className="mt-1 w-full text-[10px] text-slate-400" data-testid={`visio-journal-${a.id}`}>
                          {a.room_events.slice(-4).map((e, i) => (
                            <span key={i}>{i > 0 ? " · " : ""}{e.action === "close" ? "⚪ Fermée" : e.action === "reopen" ? "🔄 Réouverte" : "🟢 Ouverte"} {new Date(e.at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="dispo" className="mt-6 space-y-6">
          <div className="flex flex-wrap items-center gap-3 rounded-xl border border-ciel/30 bg-ciel/5 p-4" data-testid="visio-recurring">
            <RefreshCw size={16} className="text-ciel" />
            <p className="flex-1 text-xs text-slate-600">Des créneaux récurrents sont générés automatiquement (horaires par défaut) pour que le module reste toujours opérationnel. Vous pouvez aussi ajouter des créneaux manuellement ci-dessous.</p>
            <Button size="sm" data-testid="visio-gen-recurring" onClick={genRecurring} disabled={busy} className="rounded-full bg-navy text-xs text-white hover:bg-navy-light"><RefreshCw size={13} className="mr-1" /> Générer maintenant</Button>
          </div>
          {prices && (
            <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="visio-prices">
              <p className="mb-1 text-sm font-semibold text-navy">Tarifs des consultations (FCFA)</p>
              <p className="mb-4 text-xs text-slate-500">0 = gratuit. Le paiement est optionnel côté patient (carte bancaire ou Mobile Money).</p>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {(prices.session_types || []).map((t) => (
                  <div key={t.key}>
                    <label className="mb-1 block text-xs text-slate-500">{t.label}</label>
                    <Input type="number" min="0" step="500" value={prices.prices[t.key] ?? 0} data-testid={`visio-price-${t.key}`}
                      onChange={(e) => setPrices((p) => ({ ...p, prices: { ...p.prices, [t.key]: Number(e.target.value) } }))}
                      className="h-9 text-sm" />
                  </div>
                ))}
              </div>
              <Button size="sm" data-testid="visio-save-prices" onClick={savePrices} disabled={busy} className="mt-4 rounded-full bg-ciel text-xs text-white hover:bg-sky-700">Enregistrer les tarifs</Button>
            </div>
          )}
          {avail && (
            <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="visio-availability-editor">
              <div className="mb-1 flex flex-wrap items-center gap-3">
                <p className="text-sm font-semibold text-navy">Disponibilités récurrentes (grille hebdomadaire)</p>
                <label className="ml-auto flex items-center gap-1.5 text-xs text-slate-500">Horizon
                  <Input type="number" min="1" max="90" value={avail.horizon_days} data-testid="visio-avail-horizon"
                    onChange={(e) => setAvail((a) => ({ ...a, horizon_days: Number(e.target.value) }))} className="h-8 w-16 text-xs" /> jours</label>
              </div>
              <p className="mb-4 text-xs text-slate-500">Définissez des créneaux hebdomadaires par type ; ils sont générés automatiquement dans le futur.</p>
              <div className="space-y-4">
                {(avail.session_types || []).map((t) => (
                  <div key={t.key} className="rounded-lg border border-slate-100 bg-slate-50 p-3" data-testid={`visio-avail-${t.key}`}>
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-xs font-semibold text-navy">{t.label}</span>
                      <button onClick={() => addRule(t.key)} data-testid={`visio-avail-add-${t.key}`} className="rounded-full bg-navy px-2.5 py-1 text-[11px] font-medium text-white">+ Créneau</button>
                    </div>
                    {(avail.rules[t.key] || []).length === 0 ? <p className="text-[11px] text-slate-400">Aucun créneau récurrent.</p>
                      : <div className="space-y-1.5">{(avail.rules[t.key] || []).map((r, i) => (
                        <div key={i} className="flex flex-wrap items-center gap-2">
                          <select value={r.weekday} onChange={(e) => setRule(t.key, i, "weekday", Number(e.target.value))} className="h-8 rounded-lg border border-slate-200 px-1.5 text-xs">
                            {WEEKDAYS.map((d, k) => <option key={k} value={k}>{d}</option>)}
                          </select>
                          <Input type="time" value={r.time} onChange={(e) => setRule(t.key, i, "time", e.target.value)} className="h-8 w-28 text-xs" />
                          <Input type="number" min="10" step="5" value={r.duration_min} onChange={(e) => setRule(t.key, i, "duration_min", Number(e.target.value))} className="h-8 w-20 text-xs" title="Durée (min)" />
                          <Input type="number" min="1" value={r.max_participants} onChange={(e) => setRule(t.key, i, "max_participants", Number(e.target.value))} className="h-8 w-20 text-xs" title="Participants max" />
                          <button onClick={() => rmRule(t.key, i)} className="rounded-full bg-red-100 px-2 py-1 text-[11px] font-medium text-red-600">Retirer</button>
                        </div>
                      ))}</div>}
                  </div>
                ))}
              </div>
              <Button size="sm" data-testid="visio-save-avail" onClick={saveAvail} disabled={busy} className="mt-4 rounded-full bg-ciel text-xs text-white hover:bg-sky-700">Enregistrer & générer les créneaux</Button>
            </div>
          )}
          <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="visio-slot-form">
            <div className="flex items-center gap-2"><Calendar size={18} className="text-ciel" /><h3 className="font-display text-base font-semibold text-ciel">Ajouter un créneau</h3></div>
            <div className="mt-4 grid gap-3 sm:grid-cols-5">
              <div>
                <Label className="text-xs text-slate-500">Type</Label>
                <Select value={slotForm.type} onValueChange={(v) => setSlotForm({ ...slotForm, type: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="visio-slot-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs text-slate-500">Intitulé (optionnel)</Label><Input data-testid="visio-slot-title" value={slotForm.title} className="mt-1.5" placeholder="Ex : Webinaire diabète" onChange={(e) => setSlotForm({ ...slotForm, title: e.target.value })} /></div>
              <div><Label className="text-xs text-slate-500">Date & heure</Label><Input type="datetime-local" data-testid="visio-slot-start" value={slotForm.start} className="mt-1.5" onChange={(e) => setSlotForm({ ...slotForm, start: e.target.value })} /></div>
              <div><Label className="text-xs text-slate-500">Durée (min)</Label><Input type="number" value={slotForm.duration_min} className="mt-1.5 font-mono" onChange={(e) => setSlotForm({ ...slotForm, duration_min: parseInt(e.target.value) || 30 })} /></div>
              <div className="flex items-end"><Button data-testid="visio-slot-add" onClick={addSlot} disabled={busy} className="w-full rounded-full bg-ciel text-white hover:bg-sky-700"><Plus size={15} className="mr-1" /> Ajouter</Button></div>
            </div>
          </div>
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="visio-slots">
            <table className="w-full min-w-[560px] text-sm">
              <thead><tr className="border-b border-slate-200 text-left font-mono text-[11px] uppercase tracking-wider text-slate-400">
                <th className="p-3">Date</th><th className="p-3">Intitulé</th><th className="p-3">Type</th><th className="p-3">Durée</th><th className="p-3"></th>
              </tr></thead>
              <tbody>
                {slots.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-400">Aucun créneau.</td></tr>}
                {slots.map((s) => (
                  <tr key={s.id} className="cursor-pointer border-b border-slate-100 hover:bg-slate-50" data-testid={`visio-slot-${s.id}`} onClick={() => openEdit(s)}>
                    <td className="p-3 text-xs text-slate-500">{fmt(s.start)}</td>
                    <td className="p-3 text-xs text-slate-700">{s.title || <span className="text-slate-300">—</span>}</td>
                    <td className="p-3 text-xs">{typeLabel(s.type)}</td>
                    <td className="p-3 text-xs">{s.duration_min} min</td>
                    <td className="p-3">
                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        {(s.type === "webinaire" || s.type === "formation") && (
                          <button onClick={() => publishFacebook(s.id)} disabled={busy} className={s.fb_published_at ? "text-[#1877F2]" : "text-slate-400 hover:text-[#1877F2]"} title={s.fb_published_at ? "Déjà publié — republier" : "Publier sur Facebook"} data-testid={`visio-slot-fb-${s.id}`}><Facebook size={15} /></button>
                        )}
                        <button onClick={() => openEdit(s)} className="text-slate-400 hover:text-ciel" data-testid={`visio-slot-edit-${s.id}`}><Pencil size={14} /></button>
                        <button onClick={() => delSlot(s.id)} className="text-slate-400 hover:text-red-500" data-testid={`visio-slot-del-${s.id}`}><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="rappels" className="mt-6">
          <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="visio-reminder-settings">
            <div className="flex items-center gap-2">
              <BellRing size={18} className="text-ciel" />
              <h3 className="font-display text-base font-semibold text-ciel">Envoi programmable des rappels (réglage global)</h3>
            </div>
            <p className="mt-1 text-xs text-slate-500">Ces délais s'appliquent par défaut à toute nouvelle session. Vous pouvez surcharger le réglage session par session depuis l'onglet « Sessions » → bouton « Rappels ».</p>
            <div className="mt-4">
              <Label className="text-xs text-slate-500">Envoyer un rappel avant la session</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {remChoices.map((c) => {
                  const on = remSettings.offsets.includes(c.min);
                  return (
                    <button key={c.min} type="button" data-testid={`visio-rem-global-${c.min}`} onClick={() => toggleGlobalOffset(c.min)}
                      className={`rounded-full border px-3 py-1.5 text-xs transition-all ${on ? "border-ciel bg-ciel text-white" : "border-slate-200 bg-white text-slate-600 hover:border-ciel"}`}>
                      {c.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-x-8 gap-y-2">
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <Switch data-testid="visio-rem-global-email" checked={remSettings.email} onCheckedChange={(v) => setRemSettings((r) => ({ ...r, email: v }))} />
                Par email
              </label>
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <Switch data-testid="visio-rem-global-whatsapp" checked={remSettings.whatsapp} onCheckedChange={(v) => setRemSettings((r) => ({ ...r, whatsapp: v }))} />
                Par WhatsApp <span className="text-slate-400">({stats.whatsapp_mode})</span>
              </label>
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <Switch data-testid="visio-rem-global-sms" checked={remSettings.sms} onCheckedChange={(v) => setRemSettings((r) => ({ ...r, sms: v }))} />
                Par SMS <span className="text-slate-400">({stats.sms_mode})</span>
              </label>
            </div>
            <Button data-testid="visio-rem-global-save" onClick={saveRemSettings} disabled={busy} className="mt-5 rounded-full bg-ciel text-white hover:bg-sky-700">
              {busy ? "Enregistrement…" : "Enregistrer les réglages"}
            </Button>
          </div>

          <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="visio-cr-settings">
            <h3 className="font-display text-base font-semibold text-ciel">Confidentialité du compte-rendu</h3>
            <p className="mt-1 text-xs text-slate-500">Durée de validité du lien public du compte-rendu envoyé au patient. Au-delà, le lien expire automatiquement.</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {[7, 15, 30].map((d) => (
                <button key={d} type="button" data-testid={`visio-cr-days-${d}`} onClick={() => saveCrLinkDays(d)}
                  className={`rounded-full border px-4 py-1.5 text-xs transition-all ${crLinkDays === d ? "border-ciel bg-ciel text-white" : "border-slate-200 bg-white text-slate-600 hover:border-ciel"}`}>
                  {d} jours
                </button>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!editRem} onOpenChange={() => setEditRem(null)}>
        <DialogContent data-testid="visio-rem-dialog">
          <DialogHeader><DialogTitle className="font-display text-navy">Rappels — {editRem?.name}</DialogTitle>
            <DialogDescription>Choisissez les délais et canaux d'envoi des rappels pour cette session.</DialogDescription>
          </DialogHeader>
          {editRem && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-slate-500">Envoyer un rappel avant la session</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {remChoices.map((c) => {
                    const on = editRem.offsets.includes(c.min);
                    return (
                      <button key={c.min} type="button" data-testid={`visio-rem-edit-${c.min}`} onClick={() => toggleEditOffset(c.min)}
                        className={`rounded-full border px-3 py-1.5 text-xs transition-all ${on ? "border-ciel bg-ciel text-white" : "border-slate-200 bg-white text-slate-600 hover:border-ciel"}`}>
                        {c.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
                <label className="flex items-center gap-2 text-xs text-slate-600">
                  <Switch data-testid="visio-rem-edit-email" checked={editRem.email} onCheckedChange={(v) => setEditRem((r) => ({ ...r, email: v }))} />
                  Par email
                </label>
                <label className="flex items-center gap-2 text-xs text-slate-600">
                  <Switch data-testid="visio-rem-edit-whatsapp" checked={editRem.whatsapp} onCheckedChange={(v) => setEditRem((r) => ({ ...r, whatsapp: v }))} />
                  Par WhatsApp
                </label>
                <label className="flex items-center gap-2 text-xs text-slate-600">
                  <Switch data-testid="visio-rem-edit-sms" checked={editRem.sms} onCheckedChange={(v) => setEditRem((r) => ({ ...r, sms: v }))} />
                  Par SMS
                </label>
              </div>
              <div data-testid="visio-rem-history">
                <Label className="text-xs text-slate-500">Historique des rappels envoyés</Label>
                {editRem.log && editRem.log.length > 0 ? (
                  <ul className="mt-2 space-y-1.5">
                    {editRem.log.map((l, i) => (
                      <li key={i} className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-3 py-1.5 text-xs">
                        <span className="text-navy">Rappel {l.label}</span>
                        <span className="flex items-center gap-2 text-slate-400">
                          {l.email && <span className="rounded bg-sky-100 px-1.5 py-0.5 text-[10px] text-sky-700">email</span>}
                          {l.whatsapp && <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] text-emerald-700">WhatsApp</span>}
                          {l.sms && <span className="rounded bg-violet-100 px-1.5 py-0.5 text-[10px] text-violet-700">SMS</span>}
                          <span>{l.at ? new Date(l.at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : ""}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-2 rounded-lg border border-dashed border-slate-200 px-3 py-2 text-xs text-slate-400">Aucun rappel envoyé pour l'instant.</p>
                )}
              </div>
              <Button data-testid="visio-rem-edit-save" onClick={saveRemEdit} disabled={busy} className="w-full rounded-full bg-ciel text-white hover:bg-sky-700">
                {busy ? "Enregistrement…" : "Enregistrer les rappels de cette session"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editAppt} onOpenChange={() => setEditAppt(null)}>
        <DialogContent data-testid="visio-edit-appt-dialog">
          <DialogHeader><DialogTitle className="font-display text-navy">Modifier le rendez-vous</DialogTitle>
            <DialogDescription>Changez la date, l'heure, le type ou les participants. Les participants seront notifiés.</DialogDescription>
          </DialogHeader>
          {editAppt && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-slate-500">Type d'activité</Label>
                <Select value={editAppt.type} onValueChange={(v) => setEditAppt({ ...editAppt, type: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="edit-appt-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-slate-500">Date & heure</Label><Input type="datetime-local" value={editAppt.start} className="mt-1.5" data-testid="edit-appt-start" onChange={(e) => setEditAppt({ ...editAppt, start: e.target.value })} /></div>
                <div><Label className="text-xs text-slate-500">Durée (min)</Label><Input type="number" value={editAppt.duration_min} className="mt-1.5 font-mono" data-testid="edit-appt-duration" onChange={(e) => setEditAppt({ ...editAppt, duration_min: e.target.value })} /></div>
              </div>
              <div><Label className="text-xs text-slate-500">Participant (nom)</Label><Input value={editAppt.name} className="mt-1.5" data-testid="edit-appt-name" onChange={(e) => setEditAppt({ ...editAppt, name: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-slate-500">Email</Label><Input value={editAppt.email} className="mt-1.5" data-testid="edit-appt-email" onChange={(e) => setEditAppt({ ...editAppt, email: e.target.value })} /></div>
                <div><Label className="text-xs text-slate-500">WhatsApp / Téléphone</Label><Input value={editAppt.whatsapp} className="mt-1.5" data-testid="edit-appt-whatsapp" onChange={(e) => setEditAppt({ ...editAppt, whatsapp: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-slate-500">Ouverture auto (min avant, 0 = off)</Label><Input type="number" min="0" value={editAppt.auto_open_min} className="mt-1.5 font-mono" data-testid="edit-appt-auto-open" onChange={(e) => setEditAppt({ ...editAppt, auto_open_min: e.target.value })} /></div>
                <div><Label className="text-xs text-slate-500">Rappel avant ouverture</Label>
                  <select value={editAppt.preopen_reminder_min} onChange={(e) => setEditAppt({ ...editAppt, preopen_reminder_min: e.target.value })} data-testid="edit-appt-reminder" className="mt-1.5 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
                    <option value={5}>5 min avant</option><option value={10}>10 min avant</option><option value={15}>15 min avant</option>
                  </select>
                </div>
              </div>
              <Button data-testid="edit-appt-save" onClick={saveEditAppt} disabled={busy} className="w-full rounded-full bg-ciel text-white hover:bg-sky-700">{busy ? "Enregistrement…" : "Enregistrer & notifier"}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!recDialog} onOpenChange={() => setRecDialog(null)}>
        <DialogContent data-testid="visio-rec-dialog">
          <DialogHeader><DialogTitle className="font-display text-navy">Enregistrements — {recDialog?.name}</DialogTitle>
            <DialogDescription>Enregistrements cloud Daily.co de cette session.</DialogDescription>
          </DialogHeader>
          {recDialog && (
            <div className="space-y-2">
              {recDialog.recordings.length === 0 ? (
                <p className="rounded-lg border border-dashed border-slate-200 px-3 py-3 text-xs text-slate-500">{recDialog.message || "Aucun enregistrement pour cette session."}</p>
              ) : recDialog.recordings.map((r) => (
                <div key={r.id} className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-xs">
                  <span className="text-navy">{r.id?.slice(0, 8)} · {r.duration ? `${Math.round(r.duration / 60)} min` : "—"}</span>
                  <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => downloadRec(r.id)} data-testid={`visio-rec-dl-${r.id}`}><Download size={12} className="mr-1" /> Télécharger</Button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editSlot} onOpenChange={() => setEditSlot(null)}>        <DialogContent data-testid="visio-slot-edit-dialog">
          <DialogHeader><DialogTitle className="font-display text-navy">Modifier l'activité programmée</DialogTitle></DialogHeader>
          {editSlot && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-slate-500">Type</Label>
                <Select value={editSlot.type} onValueChange={(v) => setEditSlot({ ...editSlot, type: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="visio-edit-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs text-slate-500">Intitulé</Label><Input data-testid="visio-edit-title" value={editSlot.title} className="mt-1.5" onChange={(e) => setEditSlot({ ...editSlot, title: e.target.value })} /></div>
              <div><Label className="text-xs text-slate-500">Date & heure</Label><Input type="datetime-local" data-testid="visio-edit-start" value={editSlot.start} className="mt-1.5" onChange={(e) => setEditSlot({ ...editSlot, start: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-slate-500">Durée (min)</Label><Input type="number" data-testid="visio-edit-duration" value={editSlot.duration_min} className="mt-1.5 font-mono" onChange={(e) => setEditSlot({ ...editSlot, duration_min: parseInt(e.target.value) || 30 })} /></div>
                <div><Label className="text-xs text-slate-500">Participants max</Label><Input type="number" data-testid="visio-edit-max" value={editSlot.max_participants || ""} className="mt-1.5 font-mono" onChange={(e) => setEditSlot({ ...editSlot, max_participants: parseInt(e.target.value) || 1 })} /></div>
              </div>
              <Button data-testid="visio-edit-save" onClick={saveEdit} disabled={busy} className="w-full rounded-full bg-ciel text-white hover:bg-sky-700">{busy ? "Enregistrement…" : "Enregistrer les modifications"}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
